/**
 * SME Portal — Draft Reports page.
 * Workflow: Review AI-generated report → Generate Draft PPT → Send to EM for approval.
 * Shows tabs for each status including EM feedback when reports are sent back.
 * SME can preview the full report in-page before sending to EM.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Box, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Chip, CircularProgress, Alert, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Stack,
  Tabs, Tab, Divider, Grid, Card, CardContent, IconButton, LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  Send, TableChart, Slideshow, AutoAwesome,
  ArrowBack, Download, Star, Info,
  Visibility, Delete,
} from '@mui/icons-material';
import { authFetch } from '../../../api/authFetch';
import RefreshButton from '../../../components/RefreshButton';

/* â”€â”€ Types â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

interface DraftReport {
  id: string;
  assessment_id: string;
  organization_id: string | null;
  organization_name: string | null;
  category: string | null;
  domain: string | null;
  domain_score: number | null;
  version: number;
  status: string;
  file_name: string | null;
  has_excel: boolean;
  has_pptx: boolean;
  review_notes: string | null;
  reviewed_by: string | null;
  reviewed_by_name: string | null;
  reviewed_by_email: string | null;
  created_at: string | null;
  updated_at: string | null;
  submitted_by: string | null;
}

interface ReportDetail {
  id: string;
  assessment_id: string;
  organization_name: string | null;
  category: string | null;
  domain: string | null;
  domain_score: number | null;
  summary: string | null;
  recommendations: string[];
  status: string;
  review_notes: string | null;
  file_name: string | null;
  has_excel: boolean;
  has_pptx: boolean;
  submitted_by_name: string | null;
  submitted_by_email: string | null;
  created_at: string | null;
  updated_at: string | null;
  items: {
    question: string;
    score: number;
    rationale: string;
    recommendation: string;
    risk_level: string;
  }[];
}

/* â”€â”€ Constants â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

const STATUS_LABELS: Record<string, { label: string; color: 'default' | 'warning' | 'info' | 'success' | 'primary' | 'error' }> = {
  draft: { label: 'Pending Review', color: 'warning' },
  sme_reviewed: { label: 'Sent to EM — Awaiting Approval', color: 'info' },
  em_approved: { label: 'EM Approved', color: 'success' },
  playback_scheduled: { label: 'Playback Scheduled', color: 'primary' },
};

const RISK_COLORS: Record<string, string> = {
  critical: '#d32f2f',
  high: '#f44336',
  medium: '#ff9800',
  low: '#4caf50',
};

const domainLabel = (domain: string | null) =>
  domain
    ? domain.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    : '—';

const scoreColor = (score: number) =>
  score >= 4 ? '#4caf50' : score >= 3 ? '#ff9800' : '#f44336';

/* â”€â”€ Component â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

const DraftReports: React.FC = () => {
  /* list state */
  const [reports, setReports] = useState<DraftReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [tab, setTab] = useState(0);

  /* detail / preview state */
  const [selectedReport, setSelectedReport] = useState<ReportDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailSource, setDetailSource] = useState<DraftReport | null>(null);

  /* action state */
  const [generatingIds, setGeneratingIds] = useState<Set<string>>(new Set());
  const [generationLockedIds, setGenerationLockedIds] = useState<Set<string>>(new Set());
  const [submitDialog, setSubmitDialog] = useState<DraftReport | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<DraftReport | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  const tabStatuses = ['draft', 'sme_reviewed', 'em_approved', 'playback_scheduled'];
  const tabLabels = ['Pending Review', 'Sent to EM', 'EM Approved', 'Playback Scheduled'];

  /* â”€â”€ Fetch list â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

  const fetchReports = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(`/api/sme/reports/drafts?status=${tabStatuses[tab]}`);
      const data = await response.json();
      if (data.success) setReports(data.reports || []);
      else setError(data.error || 'Failed to load reports');
    } catch {
      setError('Failed to load draft reports');
    } finally {
      setLastRefreshed(new Date());
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  /* â”€â”€ Fetch single report detail â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

  const openReportDetail = async (report: DraftReport) => {
    setDetailLoading(true);
    setDetailSource(report);
    setError(null);
    try {
      const response = await authFetch(`/api/sme/reports/${report.id}/detail`);
      const data = await response.json();
      if (data.success) {
        setSelectedReport(data.report);
      } else {
        setError(data.error || 'Failed to load report detail');
      }
    } catch {
      setError('Failed to load report detail');
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedReport(null);
    setDetailSource(null);
  };

  /* â”€â”€ Actions â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

  const handleOpenExcel = async (report: DraftReport | ReportDetail) => {
    try {
      const response = await authFetch(`/api/sme/reports/${report.id}/excel`);
      if (!response.ok) { setError('Excel file not available'); return; }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = report.file_name || 'assessment_report.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch { setError('Failed to download Excel file'); }
  };

  const handleOpenPptx = async (report: DraftReport | ReportDetail) => {
    try {
      const response = await authFetch(`/api/sme/reports/${report.id}/pptx`);
      if (!response.ok) { setError('PPT file not available'); return; }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.id}.pptx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { setError('Failed to download PPT file'); }
  };

  const handleGeneratePptx = async (report: DraftReport) => {
    // Prevent duplicate submissions from multiple visible buttons for the same report.
    if (generatingIds.has(report.id) || generationLockedIds.has(report.id)) return;

    setGeneratingIds((prev) => {
      const next = new Set(prev);
      next.add(report.id);
      return next;
    });
    setGenerationLockedIds((prev) => {
      const next = new Set(prev);
      next.add(report.id);
      return next;
    });
    setError(null);
    try {
      const response = await authFetch(`/api/sme/reports/${report.id}/generate-pptx`, {
        method: 'POST',
      });
      const data = await response.json();
      if (data.success) {
        setSuccessMsg(`Draft PPT generated: ${data.file_name}`);
        fetchReports();
      } else {
        setError(data.error || 'Failed to generate PPT');
        setGenerationLockedIds((prev) => {
          const next = new Set(prev);
          next.delete(report.id);
          return next;
        });
      }
    } catch {
      setError('Failed to generate PPT report');
      setGenerationLockedIds((prev) => {
        const next = new Set(prev);
        next.delete(report.id);
        return next;
      });
    } finally {
      setGeneratingIds((prev) => {
        const next = new Set(prev);
        next.delete(report.id);
        return next;
      });
    }
  };

  const handleSubmitToEM = async () => {
    if (!submitDialog) return;
    try {
      const response = await authFetch(`/api/sme/reports/${submitDialog.id}/submit-to-em`, {
        method: 'POST',
        body: JSON.stringify({ review_notes: reviewNotes }),
      });
      if (response.ok) {
        setSuccessMsg('Draft report submitted to Engagement Manager for approval.');
        setSubmitDialog(null);
        setReviewNotes('');
        fetchReports();
      }
    } catch {
      setError('Failed to submit report');
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await authFetch(`/api/sme/reports/${deleteTarget.id}`, { method: 'DELETE' });
      if (res.ok) {
        setSuccessMsg('Report removed from list.');
        setDeleteTarget(null);
        fetchReports();
      } else {
        const data = await res.json();
        setError(data.error || 'Failed to remove report');
      }
    } catch {
      setError('Failed to remove report');
    } finally {
      setDeleting(false);
    }
  };

  /* â”€â”€ Render: Detail / Preview View â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

  if (detailLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 12 }}>
        <CircularProgress sx={{ color: '#483698' }} />
      </Box>
    );
  }

  if (selectedReport) {
    const r = selectedReport;
    const avgScore = r.domain_score ?? 0;
    const isDraft = r.status === 'draft';

    return (
      <Box>
        {/* Header bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={closeDetail}>
            <ArrowBack />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              Review AI-Generated Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {domainLabel(r.domain)} — {r.organization_name || 'Client'}
            </Typography>
          </Box>
          {isDraft && detailSource && !detailSource.has_pptx && (
            <Button
              variant="contained"
              color="secondary"
                  startIcon={generatingIds.has(r.id) ? <CircularProgress size={16} /> : <AutoAwesome />}
                  disabled={generatingIds.has(r.id) || generationLockedIds.has(r.id)}
              onClick={() => handleGeneratePptx(detailSource)}
            >
                  {generatingIds.has(r.id) ? 'Generating...' : 'Generate Draft PPT'}
            </Button>
          )}
        </Box>

        {successMsg && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>
            {successMsg}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* EM Feedback banner (when sent back) */}
        {detailSource && detailSource.reviewed_by_name && r.status === 'draft' && detailSource.review_notes && (
          <Paper sx={{ p: 2, mb: 3, backgroundColor: '#fce4ec', border: '1px solid #ef9a9a' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: '#c62828' }}>
              EM Feedback — Sent Back by {detailSource.reviewed_by_name}
            </Typography>
            <Typography variant="body2">{detailSource.review_notes}</Typography>
          </Paper>
        )}

        {/* Summary cards */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="overline" color="text.secondary">Overall Score</Typography>
                <Typography variant="h3" sx={{ fontWeight: 700, color: scoreColor(avgScore) }}>
                  {avgScore.toFixed(1)}
                </Typography>
                <Typography variant="caption" color="text.secondary">/5.0</Typography>
                <LinearProgress
                  variant="determinate"
                  value={(avgScore / 5) * 100}
                  sx={{
                    mt: 1, height: 8, borderRadius: 4,
                    '& .MuiLinearProgress-bar': { backgroundColor: scoreColor(avgScore) },
                  }}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">Category</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{r.category || '—'}</Typography>
                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" color="text.secondary">Domain</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{domainLabel(r.domain)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">Status</Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={(STATUS_LABELS[r.status] || { label: r.status }).label}
                    color={(STATUS_LABELS[r.status] || { color: 'default' }).color as any}
                    size="small"
                  />
                </Box>
                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" color="text.secondary">Date</Typography>
                <Typography variant="body2">
                  {r.updated_at ? new Date(r.updated_at).toLocaleString() : '—'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="overline" color="text.secondary">Downloads</Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {r.has_excel && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={() => handleOpenExcel(r)}
                    >
                      Excel Report
                    </Button>
                  )}
                  {r.has_pptx && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Slideshow />}
                      onClick={() => handleOpenPptx(r)}
                    >
                      PPT Report
                    </Button>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Executive Summary */}
        {r.summary && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Executive Summary
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {r.summary}
            </Typography>
          </Paper>
        )}

        {/* Assessment Items */}
        {r.items.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Assessment Details ({r.items.length} items)
            </Typography>
            {r.items.map((item, idx) => (
              <Card
                key={idx}
                variant="outlined"
                sx={{ mb: 2, borderLeft: `4px solid ${RISK_COLORS[item.risk_level] || '#9e9e9e'}` }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                      {idx + 1}. {item.question}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, ml: 2, flexShrink: 0 }}>
                      <Chip
                        icon={<Star sx={{ fontSize: 16 }} />}
                        label={`${item.score}/5`}
                        size="small"
                        sx={{
                          backgroundColor: `${scoreColor(item.score)}20`,
                          color: scoreColor(item.score),
                          fontWeight: 600,
                        }}
                      />
                      <Chip
                        label={item.risk_level.toUpperCase()}
                        size="small"
                        sx={{
                          backgroundColor: `${RISK_COLORS[item.risk_level] || '#9e9e9e'}20`,
                          color: RISK_COLORS[item.risk_level] || '#9e9e9e',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                        }}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ pl: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Rationale:</strong> {item.rationale}
                    </Typography>
                    <Typography variant="body2" color="primary" sx={{ fontStyle: 'italic' }}>
                      <strong>Recommendation:</strong> {item.recommendation}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Paper>
        )}

        {/* Recommendations */}
        {r.recommendations && r.recommendations.length > 0 && (
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              Key Recommendations
            </Typography>
            {r.recommendations.map((rec, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Info color="primary" sx={{ mt: 0.3, fontSize: 18 }} />
                <Typography variant="body2">{rec}</Typography>
              </Box>
            ))}
          </Paper>
        )}

        {/* Bottom action bar */}
        <Paper sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 2, position: 'sticky', bottom: 0 }}>
          <Button variant="outlined" onClick={closeDetail}>
            Back to List
          </Button>
          {isDraft && detailSource && (
            <>
              {!detailSource.has_pptx && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={generatingIds.has(r.id) ? <CircularProgress size={16} /> : <AutoAwesome />}
                  disabled={generatingIds.has(r.id) || generationLockedIds.has(r.id)}
                  onClick={() => handleGeneratePptx(detailSource)}
                >
                  Generate Draft PPT
                </Button>
              )}
              {detailSource.has_pptx && detailSource.status === 'draft' && (
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  onClick={() => setSubmitDialog(detailSource)}
                >
                  Send to EM
                </Button>
              )}
            </>
          )}
        </Paper>

        {/* Submit to EM Dialog */}
        <Dialog open={!!submitDialog} onClose={() => setSubmitDialog(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Submit Draft to Engagement Manager</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Submit <strong>{submitDialog?.domain ? domainLabel(submitDialog.domain) : ''}</strong> draft
              report to the EM for final approval before client playback.
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Review Notes (visible to EM)"
              placeholder="Summary of your review and any observations..."
              value={reviewNotes}
              onChange={(e) => setReviewNotes(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setSubmitDialog(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleSubmitToEM}>
              Submit to EM
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    );
  }

  /* â”€â”€ Render: Table / List View â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          Draft Reports
        </Typography>
        <RefreshButton onRefresh={fetchReports} loading={loading} lastRefreshed={lastRefreshed} />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review AI-generated assessment reports, generate the draft PowerPoint, then submit
        to the Engagement Manager for approval before playback. EM feedback appears here in real-time.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2 }}
        textColor="secondary"
        indicatorColor="secondary"
      >
        {tabLabels.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#483698' }} />
        </Box>
      ) : reports.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {tab === 0
              ? 'No draft reports pending. Reports appear here when a client submits a completed assessment.'
              : 'No reports in this category.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Assessment</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Client</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Score</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                {tab === 0 && <TableCell sx={{ fontWeight: 600 }}>EM Feedback</TableCell>}
                <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reports.map((r) => {
                const statusInfo = STATUS_LABELS[r.status] || { label: r.status, color: 'default' as const };
                const hasFeedback = r.reviewed_by_name && r.review_notes;
                return (
                  <TableRow key={r.id} hover>
                    <TableCell
                      sx={{ cursor: 'pointer' }}
                      onClick={() => openReportDetail(r)}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {domainLabel(r.domain)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{r.category}</Typography>
                    </TableCell>
                    <TableCell>{r.organization_name || '—'}</TableCell>
                    <TableCell>
                      {r.domain_score != null ? (
                        <Chip
                          label={`${r.domain_score.toFixed(1)}/5`}
                          size="small"
                          color={r.domain_score >= 4 ? 'success' : r.domain_score >= 3 ? 'warning' : 'error'}
                        />
                      ) : '—'}
                    </TableCell>
                    <TableCell>
                      <Chip label={statusInfo.label} size="small" color={statusInfo.color} />
                      {hasFeedback && r.status === 'draft' && (
                        <Chip
                          label="EM Returned"
                          size="small"
                          color="error"
                          variant="outlined"
                          sx={{ ml: 0.5 }}
                        />
                      )}
                    </TableCell>
                    {tab === 0 && (
                      <TableCell sx={{ maxWidth: 250 }}>
                        {hasFeedback ? (
                          <Tooltip title={r.review_notes || ''}>
                            <Box>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: '#c62828' }}>
                                {r.reviewed_by_name}:
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                              >
                                {r.review_notes}
                              </Typography>
                            </Box>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                    )}
                    <TableCell>
                      {r.updated_at ? new Date(r.updated_at).toLocaleDateString() : r.created_at ? new Date(r.created_at).toLocaleDateString() : '—'}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {/* Review / View full report */}
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<Visibility />}
                          sx={{
                            backgroundColor: '#483698',
                            '&:hover': { backgroundColor: '#372b7a' },
                          }}
                          onClick={() => openReportDetail(r)}
                        >
                          {r.status === 'draft' ? 'Review' : 'View'}
                        </Button>

                        {/* Download Excel */}
                        {r.has_excel && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<TableChart />}
                            onClick={() => handleOpenExcel(r)}
                          >
                            Excel
                          </Button>
                        )}

                        {/* Generate Draft PPT — only for draft status */}
                        {r.status === 'draft' && !r.has_pptx && (
                          <Button
                            size="small"
                            variant="contained"
                            color="secondary"
                            startIcon={generatingIds.has(r.id) ? <CircularProgress size={16} /> : <AutoAwesome />}
                            disabled={generatingIds.has(r.id) || generationLockedIds.has(r.id)}
                            onClick={() => handleGeneratePptx(r)}
                          >
                            {generatingIds.has(r.id) ? 'Generating...' : 'Generate PPT'}
                          </Button>
                        )}

                        {/* View PPT */}
                        {r.has_pptx && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<Slideshow />}
                            onClick={() => handleOpenPptx(r)}
                          >
                            PPT
                          </Button>
                        )}

                        {/* Submit to EM — draft with PPT only */}
                        {r.status === 'draft' && r.has_pptx && (
                          <Button
                            size="small"
                            variant="contained"
                            startIcon={<Send />}
                            onClick={() => setSubmitDialog(r)}
                          >
                            Send to EM
                          </Button>
                        )}

                        {/* Delete — for em_approved / playback_scheduled */}
                        {(r.status === 'em_approved' || r.status === 'playback_scheduled') && (
                          <Tooltip title="Remove from list">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => setDeleteTarget(r)}
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Submit to EM Dialog */}
      <Dialog open={!!submitDialog} onClose={() => setSubmitDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Submit Draft to Engagement Manager</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Submit <strong>{submitDialog?.domain ? domainLabel(submitDialog.domain) : ''}</strong> draft
            report to the EM for final approval before client playback.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Review Notes (visible to EM)"
            placeholder="Summary of your review and any observations..."
            value={reviewNotes}
            onChange={(e) => setReviewNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubmitDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmitToEM}>
            Submit to EM
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Remove Report</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{deleteTarget?.domain ? domainLabel(deleteTarget.domain) : 'this report'}</strong> from
            your list? This will hide it from view.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Removing...' : 'Remove'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DraftReports;
